/**
 * Collapsible "New device" form. Self-gates on `device.write`.
 *
 * The device value is captured in *minor units* (the unit the API DTO
 * expects). Currency is a dropdown sourced from GET /api/country-config
 * (the caller's country), so the code is always valid and never typed; the
 * multi-country path (ADR-0010) stays open because the option comes from
 * the country registry rather than a hardcoded TZS.
 *
 * Capabilities is a free-form comma-separated list (the control-provider
 * capability vocabulary is provider-specific and not yet enumerated in a
 * shared type).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { CreateDeviceRequest, Device, DeviceSource, ModelRiskClass } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { CONTROL_PROVIDERS, DEVICE_CATALOG, modelsForBrand } from "../lib/device-catalog.js";
import { useCountryConfig } from "../lib/useCountryConfig.js";

const RISK_CLASSES: readonly ModelRiskClass[] = ["A", "B", "C"];

export function CreateDeviceForm(): JSX.Element | null {
  const auth = useAuth();
  const canWrite = (auth.user?.permissions ?? []).includes("device.write");
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const cfg = useCountryConfig(open);
  const [imei, setImei] = useState("");
  const [serial, setSerial] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [amountMinor, setAmountMinor] = useState("");
  const [currency, setCurrency] = useState("");
  const [modelRiskClass, setModelRiskClass] = useState<ModelRiskClass>("B");
  const [source, setSource] = useState<DeviceSource>("warehouse");
  const [controlProvider, setControlProvider] = useState("");
  const [capabilities, setCapabilities] = useState("");
  const [downPaymentMinor, setDownPaymentMinor] = useState("");
  const [lendingPriceMode, setLendingPriceMode] = useState<"" | "per_period" | "fixed">("");
  const [lendingPriceMinor, setLendingPriceMinor] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateDeviceRequest) => api.post<Device>("/api/devices", body),
    onSuccess: () => {
      reset();
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["devices", "list"] });
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function reset(): void {
    setImei("");
    setSerial("");
    setBrand("");
    setModel("");
    setAmountMinor("");
    setCurrency("");
    setModelRiskClass("B");
    setControlProvider("");
    setCapabilities("");
    setDownPaymentMinor("");
    setLendingPriceMode("");
    setLendingPriceMinor("");
    setError(null);
  }

  function submit(): void {
    setError(null);
    if (!/^\d{15}$/.test(imei)) {
      setError("IMEI must be exactly 15 digits.");
      return;
    }
    if (serial.trim().length < 1) {
      setError("Serial is required.");
      return;
    }
    if (brand.trim().length < 1 || model.trim().length < 1) {
      setError("Brand and model are required.");
      return;
    }
    const amount = Number(amountMinor);
    if (!Number.isInteger(amount) || amount < 0) {
      setError("Value must be a non-negative whole number (minor units).");
      return;
    }
    const cur = currency.trim().toUpperCase();
    if (cur.length !== 3) {
      setError("Currency must be a 3-letter code (e.g. TZS).");
      return;
    }
    if (controlProvider.trim().length < 1) {
      setError("Control provider is required.");
      return;
    }
    const downPayment = Number(downPaymentMinor);
    if (!Number.isInteger(downPayment) || downPayment < 0) {
      setError("Down payment must be a non-negative whole number (minor units).");
      return;
    }
    if (lendingPriceMode !== "per_period" && lendingPriceMode !== "fixed") {
      setError("Select a lending price mode (per period or fixed).");
      return;
    }
    const lendingPrice = Number(lendingPriceMinor);
    if (!Number.isInteger(lendingPrice) || lendingPrice < 0) {
      setError("Lending price must be a non-negative whole number (minor units).");
      return;
    }
    const caps = capabilities
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    mutation.mutate({
      imei,
      serial: serial.trim(),
      brand: brand.trim(),
      model: model.trim(),
      value: { amountMinor: amount, currency: cur },
      source,
      modelRiskClass,
      controlProvider: controlProvider.trim(),
      capabilities: caps,
      downPaymentMinor: downPayment,
      lendingPriceMode,
      lendingPriceMinor: lendingPrice,
    });
  }

  if (!canWrite) return null;

  if (!open) {
    return <Button onClick={() => setOpen(true)}>New device…</Button>;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">New device</p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="IMEI" hint="15 digits">
          <TextInput
            inputMode="numeric"
            value={imei}
            onChange={(e) => setImei(e.target.value)}
            maxLength={15}
          />
        </Field>
        <Field label="Serial">
          <TextInput value={serial} onChange={(e) => setSerial(e.target.value)} />
        </Field>
        <Field label="Brand">
          <Select
            value={brand}
            onChange={(e) => {
              setBrand(e.target.value);
              setModel("");
            }}
          >
            <option value="">Select brand…</option>
            {DEVICE_CATALOG.map((b) => (
              <option key={b.brand} value={b.brand}>
                {b.brand}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Model" hint={brand ? "" : "select a brand first"}>
          <Select value={model} onChange={(e) => setModel(e.target.value)} disabled={!brand}>
            <option value="">Select model…</option>
            {modelsForBrand(brand).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Value" hint="minor units">
          <TextInput
            inputMode="numeric"
            value={amountMinor}
            onChange={(e) => setAmountMinor(e.target.value)}
          />
        </Field>
        <Field label="Currency">
          <Select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={!cfg.data}
          >
            <option value="">{cfg.data ? "Select currency…" : "Loading…"}</option>
            {cfg.data ? <option value={cfg.data.currency}>{cfg.data.currency}</option> : null}
          </Select>
        </Field>
        <Field label="Model risk class">
          <Select
            value={modelRiskClass}
            onChange={(e) => setModelRiskClass(e.target.value as ModelRiskClass)}
          >
            {RISK_CLASSES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Source" hint="how this device entered the platform">
          <Select value={source} onChange={(e) => setSource(e.target.value as DeviceSource)}>
            <option value="warehouse">warehouse (central stock)</option>
            <option value="dealer_provisioned">dealer-provisioned</option>
          </Select>
        </Field>
        <Field label="Control provider">
          <Select value={controlProvider} onChange={(e) => setControlProvider(e.target.value)}>
            <option value="">Select provider…</option>
            {CONTROL_PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-700">
          Financing terms{" "}
          <span className="font-normal text-slate-400">
            · minor units{cfg.data ? `, ${cfg.data.currency}` : ""}
          </span>
        </p>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Down payment">
            <TextInput
              inputMode="numeric"
              value={downPaymentMinor}
              onChange={(e) => setDownPaymentMinor(e.target.value)}
            />
          </Field>
          <Field label="Lending price mode">
            <Select
              value={lendingPriceMode}
              onChange={(e) => setLendingPriceMode(e.target.value as typeof lendingPriceMode)}
            >
              <option value="">Select mode…</option>
              <option value="per_period">Per period</option>
              <option value="fixed">Fixed (any tenor)</option>
            </Select>
          </Field>
          <Field
            label={lendingPriceMode === "per_period" ? "Price per period" : "Lending price"}
            hint={lendingPriceMode === "fixed" ? "total" : ""}
          >
            <TextInput
              inputMode="numeric"
              value={lendingPriceMinor}
              onChange={(e) => setLendingPriceMinor(e.target.value)}
            />
          </Field>
        </div>
      </div>

      <Field label="Capabilities" hint="comma-separated, optional">
        <TextInput
          value={capabilities}
          onChange={(e) => setCapabilities(e.target.value)}
          placeholder="lock, locate, wipe"
        />
      </Field>

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? "Creating…" : "Create device"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
